import React, { useEffect, useState } from 'react'
import { Card, Typography, Spin, Tag, Button, Input, Space, message, Alert, DatePicker, Divider } from 'antd'
import { ReloadOutlined, LogoutOutlined, WhatsAppOutlined, QrcodeOutlined, PauseCircleOutlined, SendOutlined, UserDeleteOutlined } from '@ant-design/icons'
import { whatsappAPI } from '../services/api'
import dayjs from 'dayjs'

const { Title, Text } = Typography

function WhatsAppConfig() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [linkPhone, setLinkPhone] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [paused, setPaused] = useState(false)
  const [pauseUntil, setPauseUntil] = useState(null)
  const [morosas, setMorosas] = useState([])
  const [morosasInfo, setMorosasInfo] = useState(null)
  const [loadingMorosas, setLoadingMorosas] = useState(false)
  const [sendingMorosas, setSendingMorosas] = useState(false)
  const [sendingId, setSendingId] = useState(null)

  useEffect(() => {
    const interval = setInterval(loadStatus, 2500)
    loadStatus()
    loadPauseStatus()
    loadMorosas()
    return () => clearInterval(interval)
  }, [])

  const loadMorosas = async () => {
    setLoadingMorosas(true)
    try {
      const res = await whatsappAPI.getMorosas()
      setMorosas(res.data.morosas || [])
      setMorosasInfo({ afterDay10: res.data.afterDay10, month: res.data.month })
    } catch (e) {}
    finally { setLoadingMorosas(false) }
  }

  const loadPauseStatus = async () => {
    try { const res = await whatsappAPI.getPauseStatus(); setPaused(res.data.paused); setPauseUntil(res.data.pauseUntil) } catch(e) {}
  }

  const loadStatus = async () => {
    try {
      const res = await whatsappAPI.getStatus()
      setStatus(res.data)
    } catch (e) {}
    finally { setLoading(false) }
  }

  const sendMorosas = async (clientIds) => {
    if (!status?.isReady) return message.warning('WhatsApp debe estar conectado')
    const isAll = !clientIds
    if (isAll) setSendingMorosas(true)
    else setSendingId(clientIds[0])
    try {
      const ids = isAll ? morosas.map(m => m.clientId) : clientIds
      const res = await whatsappAPI.sendMorosas(ids)
      const sent = res.data.sent || []
      const failed = res.data.failed || []
      if (sent.length > 0) message.success(`Recordatorio enviado a ${sent.length} alumna(s)`)
      if (failed.length > 0) message.warning(`${failed.length} no pudieron recibir el mensaje`)
      loadMorosas()
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al enviar')
    }
    finally { setSendingMorosas(false); setSendingId(null) }
  }

  const requestCode = async () => {
    if (!linkPhone || linkPhone.length < 10) return message.warning('Ingresá tu número completo')
    setRequesting(true)
    try {
      const num = linkPhone.replace(/[^0-9]/g, '')
      await whatsappAPI.connectPhone(num)
      message.info('Solicitando código...')
    } catch (error) {
      message.error(error.response?.data?.error || 'Error')
    } finally {
      setTimeout(() => setRequesting(false), 5000)
    }
  }

  const startQR = async () => {
    try {
      await whatsappAPI.startQR()
      message.info('Generando QR...')
    } catch (error) {
      message.error(error.response?.data?.error || 'Error')
    }
  }

  const handleRestart = async () => {
    try {
      await whatsappAPI.restart()
      message.info('Reiniciando...')
    } catch (error) {
      message.error('Error')
    }
  }

  const handleLogout = async () => {
    try {
      await whatsappAPI.logout()
      message.success('Sesión cerrada')
    } catch (error) {
      message.error('Error')
    }
  }

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
  return (
    <div>
      <Title level={3}><WhatsAppOutlined /> WhatsApp</Title>

      {/* Estado y conexión de WhatsApp */}
      <Card title={<span><WhatsAppOutlined /> Conexión de WhatsApp</span>} style={{ textAlign: 'center', marginBottom: 20 }}>
        {status?.isReady ? (
          <div>
            <Tag color="green" style={{ fontSize: 16, padding: '8px 20px' }}>✅ WhatsApp Conectado</Tag>
            <Text style={{ display: 'block', marginTop: 10, color: '#888', fontSize: 12 }}>
              Los recordatorios y alertas se envían automáticamente a través de WhatsApp.
            </Text>
            <div style={{ marginTop: 16 }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={handleRestart}>Reiniciar</Button>
                <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>Cerrar Sesión</Button>
              </Space>
            </div>
          </div>
        ) : status?.pairingCode ? (
          <div>
            <Title level={4}>Tu código de vinculación:</Title>
            <div style={{
              fontSize: 38, fontWeight: 'bold', letterSpacing: 8,
              padding: '16px 32px', background: '#fff',
              color: '#000', borderRadius: 12, display: 'inline-block',
              margin: '16px 0', fontFamily: 'monospace', boxShadow: '0 4px 24px rgba(255,255,255,0.15)'
            }}>
              {status.pairingCode}
            </div>
            <div style={{ marginTop: 12, textAlign: 'left', maxWidth: 380, margin: '16px auto' }}>
              <p><strong>En tu celular:</strong></p>
              <p>1. Abrí WhatsApp</p>
              <p>2. ⋮ Menú → Dispositivos vinculados</p>
              <p>3. Vincular dispositivo</p>
              <p>4. <strong>"Vincular con número de teléfono"</strong></p>
              <p>5. Ingresá el código de arriba</p>
            </div>
            <Button onClick={handleRestart}>Cancelar</Button>
          </div>
        ) : status?.qrBase64 ? (
          <div>
            <Title level={4}>Escaneá el código QR con tu WhatsApp:</Title>
            <img
              src={status.qrBase64}
              alt="QR de vinculación"
              style={{ width: 260, height: 260, borderRadius: 12, margin: '16px 0', background: '#fff', padding: 8 }}
            />
            <div style={{ textAlign: 'left', maxWidth: 380, margin: '16px auto' }}>
              <p>1. Abrí WhatsApp en tu celular</p>
              <p>2. ⋮ Menú → Dispositivos vinculados</p>
              <p>3. <strong>Vincular dispositivo</strong></p>
              <p>4. Apuntá el celular a este QR</p>
            </div>
            <Button onClick={handleRestart}>Cancelar</Button>
          </div>
        ) : status?.connectionState === 'requesting_code' || status?.connectionState === 'reconnecting' ? (
          <div>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>
              {status.connectionState === 'requesting_code' ? 'Generando código de vinculación...' : `Reconectando... (intento ${status.retryCount})`}
            </p>
            <Button onClick={handleRestart} style={{ marginTop: 8 }}>Cancelar</Button>
          </div>
        ) : (
          <div>
            <Title level={4}>Conectar WhatsApp</Title>
            <Text style={{ display: 'block', marginBottom: 16, color: '#999' }}>
              Vinculá el WhatsApp del sistema <strong>una sola vez</strong>. La sesión queda guardada y se conecta sola.
            </Text>

            <Divider plain>Opción 1: Código numérico (recomendado)</Divider>
            <p style={{ color: '#666', marginBottom: 8 }}>
              Ingresá tu número <strong>completo con código de país</strong>, sin + ni espacios.
            </p>
            <Alert
              message="Ejemplo Argentina: 5493388431158 (54 + 9 + código de área + número)"
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
            <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
              <Input
                size="large"
                placeholder="5493388431158"
                value={linkPhone}
                onChange={(e) => setLinkPhone(e.target.value)}
              />
              <Button type="primary" size="large" onClick={requestCode} loading={requesting}>
                Obtener Código
              </Button>
            </Space.Compact>

            <Divider plain>Opción 2: Escanear QR</Divider>
            <Button size="large" icon={<QrcodeOutlined />} onClick={startQR}>
              Generar código QR
            </Button>
          </div>
        )}
      </Card>

      {/* Listado de morosas */}
      <Card
        title={<span><UserDeleteOutlined /> Morosas hasta la fecha</span>}
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadMorosas} loading={loadingMorosas}>Actualizar</Button>}
        style={{ marginBottom: 16 }}
      >
        {morosasInfo?.afterDay10 ? (
          <Alert
            message={`Pasó el día 10 de ${morosasInfo.month}: las cuotas adeudadas tienen 10% de recargo`}
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        ) : (
          <Alert
            message={`Recordatorio activo: las alumnas deben abonar antes del día 10 de ${morosasInfo?.month || 'este mes'}`}
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        {morosas.length === 0 ? (
          <Text style={{ color: '#52c41a' }}>¡Todas las alumnas están al día! 🎉</Text>
        ) : (
          <div>
            <Text style={{ display: 'block', marginBottom: 8 }}>
              {morosas.length} alumna(s) adeudan el mes de {morosasInfo?.month}:
            </Text>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {morosas.map(m => (
                <div key={m.clientId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a1a1a', gap: 8 }}>
                  <div>
                    <Text style={{ fontSize: 13 }}>{m.name} {m.lastName}</Text>
                    <Text style={{ fontSize: 11, color: '#888', display: 'block' }}>
                      {m.planName} · ${m.price.toLocaleString('es-AR')}
                      {m.recargo && <Tag color="red" style={{ marginLeft: 8 }}>+10% recargo = ${m.amount.toLocaleString('es-AR')}</Tag>}
                    </Text>
                  </div>
                  <Button
                    size="small"
                    icon={<SendOutlined />}
                    onClick={() => sendMorosas([m.clientId])}
                    loading={sendingId === m.clientId}
                    disabled={!status?.isReady || sendingId !== null}
                  >
                    Enviar
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="primary"
              block
              size="large"
              icon={<SendOutlined />}
              onClick={() => sendMorosas()}
              loading={sendingMorosas}
              disabled={!status?.isReady || sendingId !== null}
              style={{ borderRadius: 10 }}
            >
              Enviar recordatorio por WhatsApp a las {morosas.length} morosas
            </Button>
          </div>
        )}
      </Card>

      {/* Vacaciones / Pausar mensajes */}
      <Card title={<span><PauseCircleOutlined /> Vacaciones / Pausar Mensajes</span>} style={{ marginBottom: 16 }}>
        {paused ? (
          <div>
            <Tag color="orange" style={{ fontSize: 14, padding: '6px 16px', marginBottom: 12 }}>
              MENSAJES PAUSADOS {pauseUntil ? `hasta ${dayjs(pauseUntil).format('DD/MM/YYYY')}` : ''}
            </Tag>
            <br />
            <Button type="primary" size="large" style={{ marginTop: 8 }} onClick={async () => {
              try {
                await whatsappAPI.resume()
                setPaused(false)
                setPauseUntil(null)
                message.success('Mensajes reactivados!')
              } catch(e) { message.error('Error') }
            }}>
              Activar mensajes
            </Button>
          </div>
        ) : (
          <div>
            <Tag color="green" style={{ fontSize: 14, padding: '6px 16px', marginBottom: 12 }}>
              Mensajes ACTIVOS
            </Tag>
            <br />
            <Text style={{ display: 'block', marginBottom: 8, marginTop: 8 }}>Pausar hasta:</Text>
            <Space>
              <DatePicker placeholder="Seleccionar fecha" onChange={(d) => setPauseUntil(d ? d.format('YYYY-MM-DD') : null)} />
              <Button danger onClick={async () => {
                if (!pauseUntil) return message.warning('Selecciona una fecha')
                try {
                  await whatsappAPI.pause(pauseUntil)
                  setPaused(true)
                  message.success('Mensajes pausados hasta ' + dayjs(pauseUntil).format('DD/MM'))
                } catch(e) { message.error('Error') }
              }}>
                Pausar mensajes
              </Button>
            </Space>
          </div>
        )}
        <Text style={{ fontSize: 12, color: '#888', marginTop: 12, display: 'block' }}>
          Cuando esta pausado, no se envian recordatorios ni alertas automaticas de WhatsApp.
        </Text>
      </Card>

      {/* Info */}
      <Card title="📅 Mensajes Automáticos">
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>9:30hs diario:</strong> Verifica vencimientos</li>
          <li><strong>Día 5, 8:00hs:</strong> Recordatorio de pago</li>
          <li><strong>Día 10, 8:00hs:</strong> Aviso de recargo 10%</li>
        </ul>
        <Text type="secondary">WhatsApp debe estar conectado para que funcionen.</Text>
      </Card>
    </div>
  )
}

export default WhatsAppConfig
